import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Eye, AlertTriangle, CheckCircle,
  Users, MessageSquare, Shield, Video, VideoOff, Mic, MicOff
} from 'lucide-react';

const INTERVIEW_API_BASE = process.env.REACT_APP_API_BASE;

function getWsUrl(sessionId) {
  const base = INTERVIEW_API_BASE || '';
  const wsBase = base
    .replace('https://', 'wss://')
    .replace('http://', 'ws://');
  return `${wsBase}/observer/ws/${sessionId}`;
}

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export default function ObserveSession() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [candidateInfo, setCandidateInfo] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [proctoringEvents, setProctoringEvents] = useState([]);
  const [currentPhase, setCurrentPhase] = useState(1);
  const [observerCount, setObserverCount] = useState(0);
  const [sessionStatus, setSessionStatus] = useState('active');
  const [initialLoading, setInitialLoading] = useState(true);

  // Host mode state
  const [interviewMode, setInterviewMode] = useState('ai');
  const [hostName, setHostName] = useState(null);
  const [modeBusy, setModeBusy] = useState(false);
  const [modeError, setModeError] = useState('');

  // WebRTC state
  const [webrtcReady, setWebrtcReady] = useState(false);
  const [webrtcConnected, setWebrtcConnected] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [observerId, setObserverId] = useState(null);
  const [hasRequestedStream, setHasRequestedStream] = useState(false);

  const wsRef = useRef(null);
  const interactionsEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const screenVideoRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const audioRef = useRef(null);
  const videoTrackCountRef = useRef(0);
  const isConnectingRef = useRef(false);
  const sessionStatusRef = useRef('active');

  // Update sessionStatusRef when sessionStatus changes
  useEffect(() => {
    sessionStatusRef.current = sessionStatus;
  }, [sessionStatus]);

  // Add video element event listeners for debugging
  useEffect(() => {
    const screenVideo = screenVideoRef.current;
    const cameraVideo = cameraVideoRef.current;

    if (screenVideo) {
      const handleScreenPlay = () => {
        console.log('[WebRTC] 📺 Screen video PLAYING',
          `${screenVideo.videoWidth}x${screenVideo.videoHeight}`);
      };
      const handleScreenError = (e) => {
        console.error('[WebRTC] Screen video ERROR:', e);
      };
      screenVideo.addEventListener('playing', handleScreenPlay);
      screenVideo.addEventListener('error', handleScreenError);
      return () => {
        screenVideo.removeEventListener('playing', handleScreenPlay);
        screenVideo.removeEventListener('error', handleScreenError);
      };
    }
  }, [screenStream]);

  useEffect(() => {
    const cameraVideo = cameraVideoRef.current;
    if (cameraVideo) {
      const handleCameraPlay = () => {
        console.log('[WebRTC] 📹 Camera video PLAYING',
          `${cameraVideo.videoWidth}x${cameraVideo.videoHeight}`);
      };
      const handleCameraError = (e) => {
        console.error('[WebRTC] Camera video ERROR:', e);
      };
      cameraVideo.addEventListener('playing', handleCameraPlay);
      cameraVideo.addEventListener('error', handleCameraError);
      return () => {
        cameraVideo.removeEventListener('playing', handleCameraPlay);
        cameraVideo.removeEventListener('error', handleCameraError);
      };
    }
  }, [cameraStream]);

  // Auto-scroll to latest interaction
  useEffect(() => {
    interactionsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [interactions]);

  // Fetch initial session state
  const fetchSessionDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${INTERVIEW_API_BASE}/observer/session/${sessionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch session details');
      const data = await response.json();

      setCandidateInfo(data.candidate);
      setInteractions(data.session?.interactions || []);
      setObserverCount(data.observer_count || 0);
      setSessionStatus(data.session?.status || 'active');
      setWebrtcReady(data.webrtc_ready || false);
      setInterviewMode(data.session?.mode || 'ai');
      setHostName(data.session?.host_name || null);

      // Determine current phase from existing data
      if (data.session?.code_submission) setCurrentPhase(4);
      else if (data.session?.system_design_submission) setCurrentPhase(3);
      else if (data.session?.image_question_submission) setCurrentPhase(2);
      else setCurrentPhase(1);

      // Load existing proctoring events
      const procEvents = data.session?.proctoring_notifications?.events || [];
      setProctoringEvents(procEvents);
    } catch (err) {
      console.error('Error fetching session details:', err);
      setConnectionError('Failed to load session details');
    } finally {
      setInitialLoading(false);
    }
  }, [sessionId]);

  // Toggle host mode (take over / release)
  const toggleHostMode = useCallback(async () => {
    setModeError('');
    setModeBusy(true);
    try {
      const token = localStorage.getItem('admin_token');
      const action = interviewMode === 'host' ? 'release' : 'take-over';
      const response = await fetch(
        `${INTERVIEW_API_BASE}/observer/host/${action}/${sessionId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to ${action}`);
      }
      // Mode state will update via WS broadcast; nothing else to do here.
    } catch (err) {
      console.error('[Host] Mode toggle failed:', err);
      setModeError(err.message || 'Failed to change mode');
    } finally {
      setModeBusy(false);
    }
  }, [sessionId, interviewMode]);

  // Create WebRTC peer connection
  const createPeerConnection = useCallback(() => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }

    console.log('[WebRTC] Creating RTCPeerConnection');
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    peerConnectionRef.current = pc;
    videoTrackCountRef.current = 0;

    // Handle incoming media tracks
    pc.ontrack = (event) => {
      console.log(`[WebRTC] ✓ Received ${event.track.kind} track:`, event.track.label,
        'readyState:', event.track.readyState,
        'muted:', event.track.muted,
        'enabled:', event.track.enabled);

      if (event.track.kind === 'video') {
        videoTrackCountRef.current++;
        const stream = new MediaStream([event.track]);

        if (videoTrackCountRef.current === 1) {
          // First video track = screen share
          console.log('[WebRTC] 📺 Setting SCREEN stream (track 1)');
          setScreenStream(stream);
          // Set srcObject with retry if ref not ready
          const setScreenSrc = () => {
            if (screenVideoRef.current) {
              screenVideoRef.current.srcObject = stream;
              console.log('[WebRTC] ✅ Screen video element srcObject set');
            } else {
              console.log('[WebRTC] ⏳ Screen video ref not ready, retrying...');
              setTimeout(setScreenSrc, 100);
            }
          };
          setScreenSrc();
        } else if (videoTrackCountRef.current === 2) {
          // Second video track = camera
          console.log('[WebRTC] 📹 Setting CAMERA stream (track 2)');
          setCameraStream(stream);
          // Set srcObject with retry if ref not ready
          const setCameraSrc = () => {
            if (cameraVideoRef.current) {
              cameraVideoRef.current.srcObject = stream;
              console.log('[WebRTC] ✅ Camera video element srcObject set');
            } else {
              console.log('[WebRTC] ⏳ Camera video ref not ready, retrying...');
              setTimeout(setCameraSrc, 100);
            }
          };
          setCameraSrc();
        }
      } else if (event.track.kind === 'audio') {
        // Audio track
        console.log('[WebRTC] 🔊 Setting AUDIO stream');
        const stream = new MediaStream([event.track]);
        const setAudioSrc = () => {
          if (audioRef.current) {
            audioRef.current.srcObject = stream;
            audioRef.current.play()
              .then(() => console.log('[WebRTC] ✅ Audio playing'))
              .catch(e => console.warn('Audio autoplay blocked:', e));
          } else {
            console.log('[WebRTC] ⏳ Audio ref not ready, retrying...');
            setTimeout(setAudioSrc, 100);
          }
        };
        setAudioSrc();
      }
    };

    // Send ICE candidates to candidate via signaling
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'webrtc_ice_candidate',
          candidate: {
            candidate: event.candidate.candidate,
            sdpMid: event.candidate.sdpMid,
            sdpMLineIndex: event.candidate.sdpMLineIndex,
          }
        }));
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('[WebRTC] ICE connection state:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        setWebrtcConnected(true);
      } else if (pc.iceConnectionState === 'failed') {
        console.error('[WebRTC] ICE connection failed');
        setWebrtcConnected(false);
        // Clear streams on failure
        setScreenStream(null);
        setCameraStream(null);
        // Allow retry
        setHasRequestedStream(false);
      } else if (pc.iceConnectionState === 'disconnected') {
        console.warn('[WebRTC] ICE connection disconnected');
        setWebrtcConnected(false);
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('[WebRTC] Connection state:', pc.connectionState);
    };

    return pc;
  }, []);

  // Request WebRTC stream from candidate
  const hasRequestedStreamRef = useRef(false);
  const requestWebRTCStream = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && !hasRequestedStreamRef.current) {
      console.log('[WebRTC] Requesting media stream from candidate');
      wsRef.current.send(JSON.stringify({ type: 'webrtc_request' }));
      hasRequestedStreamRef.current = true;
      setHasRequestedStream(true);
    } else if (hasRequestedStreamRef.current) {
      console.log('[WebRTC] ⚠️ Stream already requested, skipping duplicate request');
    }
  }, []); // No dependencies - stable reference

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    // Prevent duplicate connection attempts
    if (isConnectingRef.current) {
      console.log('[Observer] ⚠️ Already attempting to connect, skipping duplicate');
      return;
    }

    const token = localStorage.getItem('admin_token');
    if (!token) {
      setConnectionError('Not authenticated');
      return;
    }

    // Prevent duplicate connections
    if (wsRef.current && (wsRef.current.readyState === WebSocket.CONNECTING || wsRef.current.readyState === WebSocket.OPEN)) {
      console.log('[Observer] ⚠️ WebSocket already connecting/connected, skipping duplicate connection');
      return;
    }

    // Close any existing connection first
    if (wsRef.current) {
      console.log('[Observer] Closing existing WebSocket before creating new one');
      wsRef.current.close(1000);
      wsRef.current = null;
    }

    isConnectingRef.current = true;
    const wsUrl = getWsUrl(sessionId);
    console.log('[Observer] Creating new WebSocket connection to:', wsUrl);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[Observer] WebSocket opened, sending auth...');
      isConnectingRef.current = false;
      ws.send(JSON.stringify({ type: 'auth', token }));
    };

    ws.onmessage = async (event) => {
      const msg = JSON.parse(event.data);

      switch (msg.type) {
        case 'connected':
          setConnected(true);
          setConnectionError('');
          setObserverCount(msg.observer_count || 0);
          setWebrtcReady(msg.webrtc_ready || false);
          setObserverId(msg.observer_id);
          hasRequestedStreamRef.current = false; // Reset on new connection
          setHasRequestedStream(false);
          console.log('[Observer] ✅ Connected with ID:', msg.observer_id);

          // Request WebRTC stream after connecting
          setTimeout(requestWebRTCStream, 1000);
          break;

        case 'error':
          setConnectionError(msg.message);
          break;

        case 'interaction':
          setInteractions(prev => [...prev, msg.data]);
          break;

        case 'evaluation':
          setInteractions(prev => {
            const updated = [...prev];
            if (updated.length > 0) {
              const last = updated[updated.length - 1];
              updated[updated.length - 1] = {
                ...last,
                live_evaluation: {
                  score: msg.data.score,
                  needs_followup: msg.data.needs_followup,
                  evaluation_notes: msg.data.evaluation_notes
                }
              };
            }
            return updated;
          });
          break;

        case 'phase_change':
          setCurrentPhase(msg.data.completed_phase + 1);
          break;

        case 'phase_submission':
          setCurrentPhase(msg.data.phase);
          break;

        case 'proctoring_event':
          setProctoringEvents(prev => [...prev, msg.data]);
          break;

        case 'session_ended':
          setSessionStatus('completed');
          break;

        case 'mode_change':
          setInterviewMode(msg.mode || 'ai');
          setHostName(msg.host_name || null);
          break;

        // --- WebRTC Signaling ---

        case 'webrtc_offer':
          console.log('[WebRTC] Received offer from candidate');
          try {
            // Check if we already have a peer connection
            let pc = peerConnectionRef.current;

            if (pc) {
              const state = pc.connectionState;
              const iceState = pc.iceConnectionState;

              // If already connected or connecting, ignore duplicate offer
              if (state === 'connected' || state === 'connecting' ||
                  iceState === 'connected' || iceState === 'checking') {
                console.log(`[WebRTC] ⚠️ Already have active connection (state: ${state}, ice: ${iceState}), ignoring duplicate offer`);
                break;
              }

              // Close failed/old connection
              console.log(`[WebRTC] Closing old peer connection (state: ${state}, ice: ${iceState})`);
              pc.close();
              peerConnectionRef.current = null;
              videoTrackCountRef.current = 0;
            }

            pc = createPeerConnection();
            await pc.setRemoteDescription(new RTCSessionDescription(msg.offer));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);

            // Send answer back to candidate
            ws.send(JSON.stringify({
              type: 'webrtc_answer',
              answer: {
                type: answer.type,
                sdp: answer.sdp
              }
            }));
            console.log('[WebRTC] Sent answer to candidate');
          } catch (err) {
            console.error('[WebRTC] Error handling offer:', err);
          }
          break;

        case 'webrtc_ice_candidate':
          if (peerConnectionRef.current && msg.candidate) {
            try {
              await peerConnectionRef.current.addIceCandidate(
                new RTCIceCandidate(msg.candidate)
              );
            } catch (err) {
              console.warn('[WebRTC] Error adding ICE candidate:', err);
            }
          }
          break;

        case 'pong':
          break;

        default:
          break;
      }
    };

    ws.onclose = (event) => {
      console.log(`[Observer] WebSocket closed - Code: ${event.code}, Reason: "${event.reason || 'none'}", Clean: ${event.wasClean}`);
      isConnectingRef.current = false;
      setConnected(false);
      if (event.code !== 1000 && sessionStatusRef.current === 'active') {
        console.log('[Observer] Will reconnect in 3 seconds...');
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      } else {
        console.log('[Observer] Not reconnecting (clean close or session ended)');
      }
    };

    ws.onerror = (error) => {
      console.error('[Observer] WebSocket error:', error);
      isConnectingRef.current = false;
      setConnected(false);
    };

    // Ping every 30 seconds
    pingIntervalRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }, [sessionId, createPeerConnection, requestWebRTCStream]); // Removed sessionStatus to prevent reconnects

  useEffect(() => {
    fetchSessionDetails();

    // Only connect WebSocket once on mount
    connectWebSocket();

    return () => {
      console.log('[Observer] 🧹 Component unmounting - cleaning up...');
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]); // Only depend on sessionId, not the callback functions

  const phases = [
    { num: 1, name: 'Q&A' },
    { num: 2, name: 'Image' },
    { num: 3, name: 'Design' },
    { num: 4, name: 'Code' },
  ];

  const getSeverityColor = (severity) => {
    if (severity >= 8) return 'bg-red-100 text-red-800 border-red-200';
    if (severity >= 5) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-brand-500/15 text-blue-800 border-blue-200';
  };

  const getScoreColor = (score) => {
    if (score >= 7) return 'bg-green-100 text-green-700';
    if (score >= 4) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-ink-800 flex items-center justify-center">
        <p className="text-ink-400">Loading session...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink-800">
      {/* Audio element (hidden, auto-plays) */}
      <audio ref={audioRef} autoPlay />

      {/* Header */}
      <header className="bg-ink-900 shadow-sm border-b border-ink-700">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-1 text-ink-300 hover:text-ink-100 text-sm transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <h1 className="text-lg font-semibold text-ink-100 flex items-center gap-2">
                <Eye className="w-5 h-5 text-brand-400" />
                Observing: {candidateInfo?.name || 'Unknown'}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                connected
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                }`} />
                {connected ? 'Connected' : 'Disconnected'}
              </div>
              {webrtcConnected && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <Video className="w-3 h-3" />
                  Live Media
                </div>
              )}
              {interviewMode === 'host' && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  <Mic className="w-3 h-3" />
                  Host: {hostName || 'You'}
                </div>
              )}
              {sessionStatus === 'active' && (
                <button
                  onClick={toggleHostMode}
                  disabled={modeBusy}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors disabled:opacity-50 ${
                    interviewMode === 'host'
                      ? 'bg-ink-700 text-ink-100 hover:bg-gray-300'
                      : 'bg-amber-600 text-white hover:bg-amber-700'
                  }`}
                  title={interviewMode === 'host' ? 'Hand back to AI' : 'Take over from AI'}
                >
                  {interviewMode === 'host' ? (
                    <><MicOff className="w-3 h-3" /> Release to AI</>
                  ) : (
                    <><Mic className="w-3 h-3" /> Take Over</>
                  )}
                </button>
              )}
              <div className="flex items-center gap-1 text-sm text-ink-400">
                <Users className="w-4 h-4" />
                {observerCount}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Error banner */}
      {connectionError && (
        <div className="max-w-7xl mx-auto px-4 mt-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {connectionError}
          </div>
        </div>
      )}

      {modeError && (
        <div className="max-w-7xl mx-auto px-4 mt-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {modeError}
          </div>
        </div>
      )}

      {/* Session ended banner */}
      {sessionStatus === 'completed' && (
        <div className="max-w-7xl mx-auto px-4 mt-3">
          <div className="bg-brand-500/10 border border-blue-200 rounded-lg p-3 flex items-center gap-2 text-brand-300 text-sm">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            This interview session has ended.
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Live Media Display */}
        <div className="bg-ink-900 rounded-lg shadow-sm border border-ink-700 p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-ink-100 flex items-center gap-2">
              <Video className="w-4 h-4 text-brand-400" />
              Live Media Stream
            </h3>
            {!webrtcReady && (
              <span className="text-xs text-yellow-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Waiting for candidate to connect...
              </span>
            )}
          </div>

          <div className="relative bg-gray-900 rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
            {/* Screen Share (main view) */}
            {screenStream ? (
              <video
                ref={screenVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-ink-500">
                <VideoOff className="w-12 h-12 mb-2" />
                <p className="text-sm">
                  {webrtcReady ? 'Waiting for media stream...' : 'Candidate not ready yet'}
                </p>
                {webrtcReady && (
                  <button
                    onClick={requestWebRTCStream}
                    className="mt-3 px-4 py-2 bg-brand-500 text-white text-sm rounded hover:bg-brand-400 transition-colors"
                  >
                    Request Stream
                  </button>
                )}
              </div>
            )}

            {/* Camera (PIP overlay) */}
            {cameraStream && (
              <div className="absolute bottom-4 right-4 w-48 h-36 bg-gray-800 rounded-lg overflow-hidden border-2 border-white shadow-lg">
                <video
                  ref={cameraVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
            )}
          </div>
        </div>

        {/* Phase indicator */}
        <div className="bg-ink-900 rounded-lg shadow-sm border border-ink-700 p-4 mb-6">
          <div className="flex items-center gap-2">
            {phases.map((phase) => (
              <div
                key={phase.num}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-all ${
                  currentPhase === phase.num
                    ? 'bg-brand-500 text-white font-medium'
                    : currentPhase > phase.num
                      ? 'bg-green-100 text-green-700'
                      : 'bg-ink-800 text-ink-400'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold border ${
                  currentPhase === phase.num ? 'border-white/30' : 'border-current/30'
                }`}>
                  {currentPhase > phase.num ? '✓' : phase.num}
                </span>
                {phase.name}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Live Transcript */}
          <div className="lg:col-span-2 bg-ink-900 rounded-lg shadow-sm border border-ink-700 flex flex-col" style={{ maxHeight: '70vh' }}>
            <div className="px-4 py-3 border-b border-ink-700 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-brand-400" />
              <h3 className="font-medium text-ink-100">Live Transcript</h3>
              <span className="text-xs text-ink-500 ml-auto">
                {interactions.length} interactions
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {interactions.length === 0 ? (
                <p className="text-center text-ink-500 py-8">
                  Waiting for interview interactions...
                </p>
              ) : (
                interactions.map((interaction, idx) => (
                  <div key={idx} className="space-y-2">
                    {/* Question */}
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-brand-500/15 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-brand-300">AI</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-ink-100">
                          {interaction.question}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-ink-500 capitalize">
                            {interaction.question_type?.replace(/_/g, ' ')}
                          </span>
                          {(interaction.evaluation?.score !== undefined || interaction.live_evaluation?.score !== undefined) && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${getScoreColor(
                              interaction.live_evaluation?.score ?? interaction.evaluation?.score
                            )}`}>
                              Score: {interaction.live_evaluation?.score ?? interaction.evaluation?.score}/10
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Answer */}
                    <div className="flex gap-3 ml-4">
                      <div className="w-8 h-8 bg-ink-800 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-ink-300">C</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-ink-300">
                          {interaction.candidate_response}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={interactionsEndRef} />
            </div>
          </div>

          {/* Right: Proctoring Events */}
          <div className="bg-ink-900 rounded-lg shadow-sm border border-ink-700 flex flex-col" style={{ maxHeight: '70vh' }}>
            <div className="px-4 py-3 border-b border-ink-700 flex items-center gap-2">
              <Shield className="w-4 h-4 text-orange-600" />
              <h3 className="font-medium text-ink-100">Proctoring</h3>
              <span className="text-xs text-ink-500 ml-auto">
                {proctoringEvents.length} events
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {proctoringEvents.length === 0 ? (
                <p className="text-center text-ink-500 py-8 text-sm">
                  No proctoring events yet
                </p>
              ) : (
                proctoringEvents.map((event, idx) => (
                  <div
                    key={idx}
                    className={`p-2.5 rounded border text-xs ${getSeverityColor(event.severity)}`}
                  >
                    <div className="flex justify-between items-start">
                      <span className="font-medium">
                        {event.event_type?.replace(/_/g, ' ').toUpperCase()}
                      </span>
                      <span className="opacity-60">
                        Sev: {event.severity}
                      </span>
                    </div>
                    {event.timestamp && (
                      <p className="mt-1 opacity-75">
                        {new Date(event.timestamp).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
